import { Alert, Button, Grid, Typography } from "@mui/material"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Option } from "../../services/ntuc/userService/staticData"
import { ActionType } from "../../store/signup/SignupReducer"
import { useSignupStore } from "../../store/signup/UserSignupStore"
import { CancelButton } from "../CancelButton/CancelButton"
import { SaveAndContinueButton } from "../SaveAndContinueButton/SaveAndContinueButton"
import { BankAccountNumberField } from "./FormFields/BankAccountNumberField"
import { BankNameField } from "./FormFields/BankNameField"
import { saveUserMembershipInfo } from "../../services/ntuc/memberService/membershipSignup"
import { Status } from "../../services/ntuc/Model"
import TabProps from "../TabProps/TabProps"
import ErrorDialog from "../Dialog/ErrorDialog"
import { useDialogErrorHandler, useAlertErrorHandler } from "../../hooks/ErrorHandler"
import { ErrorCode } from "../../services/ntuc/applicationError"
import { getSignupRequest } from "../../helpers/SignupUtils"
import BankDetailsValidator, { BankDetailsFormValidation } from "../../services/validation/bank-details-form/BankDetailsValidator"
import { SignupTabs } from "../../views/signup-flow/SignupFormPage/SignupFormPage"

enum DialogButtonAction {
    BACK = `back_to_home`,
    LOGIN = `login`,
    CANCEL = `cancel`,
    DRAFT = `retrieve_draft`,
    CONTINUE = `continue`,
    LOGIN_ANOTHER = `login_another`,
}

const BankDetails = (props: TabProps) => {
    const { changeTab } = props
    const { t } = useTranslation([`signup`, `common`])
    const [bankNameErrorMessage, setBankNameErrorMessage] = useState<string | undefined>()
    const [accountNumberErrorMessage, setAccountNumberErrorMessage] = useState<string | undefined>()
    const [state, dispatch] = useSignupStore()
    const [isLoading, setIsLoading] = useState<boolean>(false)

    const [alert, alertErrorHandler] = useAlertErrorHandler()

    const [dialog, dialogErrorHandler, closeHandler] = useDialogErrorHandler(
        {
            content: ``,
            title: ``,
            display: false,
        },
    )

    const handleDialogClick = (action?: string): void => {
        switch (action) {
            case DialogButtonAction.BACK:
                if (window.myNtucConfig.homeUrl) {
                    window.location.replace(window.myNtucConfig.homeUrl)
                }
                return
            case DialogButtonAction.LOGIN_ANOTHER:
                if (window.myNtucConfig.loginUrl) {
                    window.location.replace(window.myNtucConfig.loginUrl)
                }
                return
            case DialogButtonAction.CANCEL:
                return closeHandler()
            default:
                break
        }
    }

    const BankDetailsTitle1 = () => {
        return (
            <Typography variant="body1" paddingTop={2}>
                {t(`bank_details_notes_1`)}
            </Typography>
        )
    }

    const BankDetailsTitle2 = () => {
        return (
            <Typography variant="body1" paddingTop={1}>
                {t(`bank_details_notes_2`)}
            </Typography>
        )
    }

    const TermsAndConditionTitle = () => {
        return (<p>
            <Typography variant="body2" paddingTop={1}>
                {t(`bank_details_can_change`)}
            </Typography>
            <Typography variant="body2" paddingTop={1}>
                {t(`bank_details_tnc`)}
            </Typography>
        </p>
        )
    }

    const handleNavigateBack = () => {
        changeTab?.(`employment`)
    }

    const handleSaveAnContinueClick = () => {
        BankDetailsValidator(state)
            .then(() => {
                setIsLoading(true)
                saveForm()
            })
            .catch(validationResult => {
                displayErrorMessages(validationResult)
            })
    }

    const saveForm = () => {
        const request = getSignupRequest(state)

        saveUserMembershipInfo(request, state.membershipId)
            .then(response => {
                if (response?.metadata.status === Status.SUCCESS) {
                    changeTab?.(SignupTabs.REFERRAL)
                } else {
                    alertErrorHandler()
                }
            })
            .catch(error => {
                if (error.response) {
                    alertErrorHandler(error.response.data.content)
                    dialogErrorHandler(error.response.data.content)
                } else if (error.errorCode in ErrorCode) {
                    alertErrorHandler(error)
                    dialogErrorHandler(error)
                }
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const displayErrorMessages = (validationResult: BankDetailsFormValidation) => {
        setAccountNumberErrorMessage(validationResult.accountNumberValidationMessage)
        setBankNameErrorMessage(validationResult.bankNameValidationMessage)
    }

    const handleBankNameChange = (name: Option) => {
        dispatch({
            type: ActionType.ADD_BANK_DETAILS,
            value: {
                ...state.bankDetail,
                bankName: name,
            },
        })
    }

    const handleAccountNumberChange = (value: string) => {
        dispatch({
            type: ActionType.ADD_BANK_DETAILS,
            value: {
                ...state.bankDetail,
                accountNumber: value,
            },
        })
    }

    return (
        <Grid
            container
            rowSpacing={4}
            columnSpacing={{
                xs: `16px`,
                sm: `24px`,
                md: `24px`,
                lg: `40px`,
            }}>
            <ErrorDialog open={dialog.display} content={t(dialog.content, { ns: `common` })} title={t(dialog.title, { ns: `common` })} onClose={closeHandler}>
                {dialog.primaryButton && <Button onClick={() => handleDialogClick(dialog.primaryButton)} fullWidth variant="contained">{t(dialog.primaryButton, { ns: `common` })}</Button>}
                {dialog.secondaryButton && <Button onClick={() => handleDialogClick(dialog.secondaryButton)} fullWidth>{t(dialog.secondaryButton, { ns: `common` })}</Button>}
            </ErrorDialog>
            <Grid item xs={12} sm={12} md={12} lg={12}>
                {alert && alert.display && <Alert severity="error">{t(alert.content)}</Alert>}
            </Grid>
            <Grid item xs={12} sm={12}>
                <BankDetailsTitle1 />
                <BankDetailsTitle2 />
                <TermsAndConditionTitle />
            </ Grid>
            <Grid item xs={12} sm={12} md={6} lg={6}>
                <BankNameField
                    defaultValue={state.bankDetail.bankName}
                    handleChange={handleBankNameChange}
                    errorText={bankNameErrorMessage} />
            </Grid>
            <Grid item xs={12} sm={12} md={6} lg={6}>
                <BankAccountNumberField
                    defaultValue={state.bankDetail.accountNumber}
                    handleChange={handleAccountNumberChange}
                    errorText={accountNumberErrorMessage}
                    bankName={state.bankDetail.bankName}
                />
            </Grid>
            <Grid item xs={12} sm={12} md={6} lg={6} textAlign="center">
                <CancelButton
                    onClick={handleNavigateBack} />
            </Grid>
            <Grid item xs={12} sm={12} md={6} lg={6} textAlign="center">
                <SaveAndContinueButton
                    loading={isLoading}
                    onClick={handleSaveAnContinueClick}
                />
            </Grid>
        </ Grid>
    )
}

export { BankDetails }
